import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveProfile } from "@/hooks/useActiveProfile";
import type { Platform } from "@/types/platform";
import { logger } from "@/lib/logger";

export type { Platform };

interface PlatformAccessRecord {
  id: string;
  user_id: string;
  platform: string;
  tier: string;
  access_type: string;
  granted_by: string | null;
  reason: string | null;
  starts_at: string;
  expires_at: string | null;
  is_active: boolean;
  stripe_subscription_id: string | null;
  ai_access: boolean;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

type PlatformAccessState = Record<Platform, PlatformAccessRecord | null>;

const ALL_PLATFORMS: Platform[] = ["callback_zone", "coach", "castings", "pro", "draftline", "hmu", "crew", "capture"];

const emptyState = (): PlatformAccessState =>
  Object.fromEntries(ALL_PLATFORMS.map((p) => [p, null])) as PlatformAccessState;

/**
 * Access Precedence per core memory:
 *   0 (Admin Overrides) > 1 (Entitlements Table) > 2 (Legacy Subscriptions)
 *
 * Lower number = higher precedence. Unknown `access_type` values fall to the
 * back so legacy rows can never accidentally beat an admin override.
 */
const ACCESS_PRECEDENCE: Record<string, number> = {
  admin_override: 0,
  admin_grant: 0,
  admin_revoke: 0,
  entitlement: 1,
  subscription: 1,
  trial: 1,
  legacy: 2,
  legacy_subscription: 2,
};

const precedenceRank = (accessType: string | null | undefined): number => {
  if (!accessType) return 99;
  return ACCESS_PRECEDENCE[accessType] ?? 99;
};

/**
 * Treat an admin `revoke`-style record as "no access" regardless of `is_active`.
 * `is_active=false` is the canonical signal, but some override patterns keep
 * the row active while flagging revocation in `access_type` / `reason`.
 */
const isRevocation = (record: PlatformAccessRecord): boolean => {
  if (record.access_type === "admin_revoke") return true;
  if (record.access_type === "admin_override" && /revoke|deny|block/i.test(record.reason ?? "")) {
    return true;
  }
  return false;
};

/**
 * Checks the current user's active access records across all eight platforms.
 * Returns per-platform access state with helpers to check access and tier.
 */
export const usePlatformAccess = () => {
  const { user } = useAuth();
  const { effectiveUserId } = useActiveProfile();
  const [access, setAccess] = useState<PlatformAccessState>(emptyState());
  const [isLoading, setIsLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    const targetId = effectiveUserId || user?.id || null;
    if (!targetId) {
      setAccess(emptyState());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("platform_access")
        .select("id, user_id, platform, tier, access_type, granted_by, reason, starts_at, expires_at, is_active, stripe_subscription_id, ai_access, metadata, created_at, updated_at")
        .eq("user_id", targetId);

      if (error) {
        logger.error("[usePlatformAccess] Error fetching access", error);
        setAccess(emptyState());
      } else {
        const state = emptyState();
        // Group by platform, then pick the highest-precedence row per platform.
        // Admin overrides (incl. revocations) beat entitlements beat legacy.
        const byPlatform = new Map<Platform, PlatformAccessRecord[]>();
        for (const record of (data || []) as PlatformAccessRecord[]) {
          if (!ALL_PLATFORMS.includes(record.platform as Platform)) continue;
          const arr = byPlatform.get(record.platform as Platform) ?? [];
          arr.push(record);
          byPlatform.set(record.platform as Platform, arr);
        }
        for (const [platform, rows] of byPlatform) {
          rows.sort((a, b) => {
            const r = precedenceRank(a.access_type) - precedenceRank(b.access_type);
            if (r !== 0) return r;
            // Tiebreak: most recent wins within the same precedence tier.
            return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
          });
          const winner = rows[0];
          // A winning revocation row denies access; expose null so the helper
          // short-circuits to "no access" without falling through to a legacy row.
          state[platform] = isRevocation(winner) ? null : winner;
        }
        setAccess(state);
      }
    } catch (error) {
      logger.error("[usePlatformAccess] Error fetching access (exception)", error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, effectiveUserId]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  // Re-evaluate access when the tab regains focus so expiring entitlements
  // and webhook-driven changes don't leave a stale grant on screen.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchAccess();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [fetchAccess]);

  const hasPlatformAccess = useCallback(
    (platform: Platform): boolean => {
      const record = access[platform];
      if (!record || !record.is_active) return false;

      const now = new Date();
      const startsAt = new Date(record.starts_at);
      if (startsAt > now) return false;

      if (record.expires_at) {
        const expiresAt = new Date(record.expires_at);
        if (expiresAt <= now) return false;
      }

      return true;
    },
    [access]
  );

  const getPlatformTier = useCallback(
    (platform: Platform): string | null => {
      if (!hasPlatformAccess(platform)) return null;
      return access[platform]?.tier || null;
    },
    [access, hasPlatformAccess]
  );

  return {
    access,
    isLoading,
    hasPlatformAccess,
    getPlatformTier,
    refetch: fetchAccess,
  };
};

/**
 * Admin-only hook for granting, revoking, and listing platform access records.
 */
export const useAdminPlatformAccess = () => {
  const grantAccess = async (
    userId: string,
    platform: Platform,
    tier?: string,
    reason?: string,
    expiresAt?: Date
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc("admin_grant_access", {
        p_user_id: userId,
        p_platform: platform,
        p_tier: tier || null,
        p_reason: reason || null,
        p_expires_at: expiresAt?.toISOString() || null,
      });

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      logger.error("Error granting access", error);
      return { success: false, error: error.message };
    }
  };

  const revokeAccess = async (
    userId: string,
    platform: Platform,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.rpc("admin_revoke_access", {
        p_user_id: userId,
        p_platform: platform,
        p_reason: reason || null,
      });

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      logger.error("Error revoking access", error);
      return { success: false, error: error.message };
    }
  };

  const fetchAllAccess = async (platform: Platform) => {
    const { data, error } = await supabase
      .from("platform_access")
      .select(`
        *,
        user:profiles!platform_access_granted_by_fkey(id, email, first_name, last_name)
      `)
      .eq("platform", platform)
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) {
      logger.error(`Error fetching ${platform} access`, error);
      // Fallback: fetch without the join
      const { data: fallbackData, error: fallbackError } = await supabase
        .from("platform_access")
        .select("id, user_id, platform, tier, access_type, is_active, starts_at, expires_at, stripe_subscription_id, granted_by, reason, created_at, updated_at")
        .eq("platform", platform)
        .order("created_at", { ascending: false })
        .limit(200);

      if (fallbackError) {
        logger.error("Fallback also failed", fallbackError);
        return [];
      }
      return fallbackData || [];
    }
    return data || [];
  };

  return {
    grantAccess,
    revokeAccess,
    fetchAllAccess,
  };
};
